import { DatePicker, Pagination, Select, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Topbar } from '../../layouts/Topbar';
import {
  getTenantBreakdown,
  getTenantDaily,
  getTenantUsage,
  usageQueryKeys,
  type AdminUsageItem,
  type UsageListParams,
} from '../../api/usage';
import { CreditActions } from './CreditActions';
import { UsageBreakdownTable } from './UsageBreakdownTable';
import { renderCredits } from './format';
import { CreditsTrend } from './CreditsTrend';
import { shanghaiTodayText, usagePageSize, usagePagination } from '../../../../../libs/shared/src/billing/display';
import { useAuthStore } from '../../store/auth';
import './usage.css';


export function TenantUsageDetail() {
  const { id } = useParams<{ id: string }>();
  const tenantId = Number(id);
  const [params, setParams] = useSearchParams();
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const from = params.get('from') || '';
  const to = params.get('to') || '';
  const page = Math.max(1, Number(params.get('page') || 1) || 1);
  const pageSize = usagePageSize(params.get('pageSize'));
  const modelPage = Math.max(1, Number(params.get('modelPage') || 1) || 1);
  const modelPageSize = usagePageSize(params.get('modelPageSize'));
  const agentKey = params.get('agentKey') || undefined;
  const today = dayjs(shanghaiTodayText());
  const range = {
    from: from || today.subtract(13, 'day').format('YYYY-MM-DD'),
    to: to || today.format('YYYY-MM-DD'),
    agentKey,
    model: params.get('model') || undefined,
    providerKey: params.get('providerKey') || undefined,
  };
  function patch(values: Record<string, string | undefined>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  }
  const itemFilter: UsageListParams = {
    ...range,
    page,
    pageSize,
    sort: 'createdAt',
  };
  const breakdownFilter: UsageListParams = {
    ...range,
    page: modelPage,
    pageSize: modelPageSize,
    sort: 'creditsUsed',
  };
  const dailyFilter: UsageListParams = {
    ...range,
    page: 1,
    pageSize: 20,
    sort: 'createdAt',
  };

  const summaryQuery = useQuery({
    queryKey: usageQueryKeys.tenant(tenantId, itemFilter),
    queryFn: () => getTenantUsage(tenantId, itemFilter),
    enabled: Number.isFinite(tenantId),
  });
  const breakdownQuery = useQuery({
    queryKey: usageQueryKeys.breakdown(tenantId, breakdownFilter),
    queryFn: () => getTenantBreakdown(tenantId, breakdownFilter),
    enabled: Number.isFinite(tenantId),
  });
  const dailyQuery = useQuery({
    queryKey: usageQueryKeys.daily(tenantId, dailyFilter),
    queryFn: () => getTenantDaily(tenantId, dailyFilter),
    enabled: Number.isFinite(tenantId),
  });

  const failed = summaryQuery.isError;
  const summary = summaryQuery.data;
  const backTo = `/usage?${params.get('back') ?? new URLSearchParams({ from: range.from, to: range.to }).toString()}`;

  const itemColumns: ColumnsType<AdminUsageItem> = [
    { title: '时间（北京时间）', dataIndex: 'date', width: 200 },
    { title: '数字员工', dataIndex: 'agentName', width: 180, render: (name: string, row) => name || row.agentKey },
    { title: '积分', dataIndex: 'creditsUsed', width: 140, render: (v: string | null, row) => row.source === 'unpriced' ? <Tag>未计费</Tag> : renderCredits(v) },
  ];

  return (
    <>
      <Topbar crumbs={[{ label: '运营管理' }, { label: '用量管理', href: backTo }, { label: `租户 ${tenantId}` }]} />
      <div className="page usage-page">
        <div className="page-header">
          <div className="page-header__title">
            <h1>租户用量</h1>
          </div>
          <div className="page-header__actions">
            <Link to={backTo} className="btn btn-secondary">返回</Link>
            <button type="button" className="btn btn-ghost" onClick={() => { void summaryQuery.refetch(); void breakdownQuery.refetch(); void dailyQuery.refetch(); }} disabled={summaryQuery.isFetching}>
              <ReloadOutlined spin={summaryQuery.isFetching} /> <span>刷新</span>
            </button>
            {summary && (
              <CreditActions tenantId={tenantId} isUnlimited={summary.isUnlimited} canWriteMode={isAdmin} />
            )}
          </div>
        </div>

        <div className="usage-toolbar">
          <DatePicker.RangePicker value={[dayjs(range.from), dayjs(range.to)]} allowClear={false} disabledDate={(current, info) => Boolean(info.from && Math.abs(current.diff(info.from, 'day')) < 6)} onChange={(dates) => {
            if (dates?.[0] && dates[1]) patch({ from: dates[0].format('YYYY-MM-DD'), to: dates[1].format('YYYY-MM-DD'), page: '1', modelPage: '1' });
          }} />
          <Select allowClear placeholder="数字员工" style={{ minWidth: 180 }} value={agentKey}
            options={(summary?.agentOptions ?? []).map((agent) => ({ value: agent.agentKey, label: agent.agentName }))}
            onChange={(key) => patch({ agentKey: key, page: '1', modelPage: '1' })} />
        </div>
        {failed && (
          <div className="usage-error">
            <span>用量查询失败</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => summaryQuery.refetch()}>重试</button>
          </div>
        )}

        {summary && (
          <>
            <div className="usage-kpis">
              <div className="kpi">
                <div className="kpi__label">当前余额</div>
                <div className="kpi__value">{renderCredits(summary.balance, summary.isUnlimited)}</div>
              </div>
              <div className="kpi">
                <div className="kpi__label">累计消耗积分</div>
                <div className="kpi__value">{renderCredits(summary.totalCreditsUsed)}</div>
              </div>
              <div className="kpi">
                <div className="kpi__label">期间消耗积分</div>
                <div className="kpi__value">{renderCredits(summary.periodCreditsUsed)}</div>
              </div>
              <div className="kpi">
                <div className="kpi__label">今日消耗积分</div>
                <div className="kpi__value">{renderCredits(summary.todayCreditsUsed)}</div>
              </div>
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card__header"><div className="card__title">趋势</div></div>
              <div className="card__body">
                {dailyQuery.isError ? (
                  <div className="usage-error">
                    <span>趋势查询失败</span>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => dailyQuery.refetch()}>重试</button>
                  </div>
                ) : dailyQuery.data ? (
                  <CreditsTrend data={dailyQuery.data.items} />
                ) : null}
              </div>
            </div>

            <div className="section">
              <div className="section-title"><h2>模型构成</h2></div>
              {breakdownQuery.isError ? (
                <div className="usage-error">
                  <span>模型构成查询失败</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => breakdownQuery.refetch()}>重试</button>
                </div>
              ) : (
                <><UsageBreakdownTable
                  rows={breakdownQuery.data?.items ?? []}
                  loading={breakdownQuery.isLoading}
                /><Pagination {...usagePagination} current={modelPage} pageSize={modelPageSize} total={breakdownQuery.data?.total ?? 0}
                  style={{ marginTop: 16, textAlign: 'right' }}
                  onChange={(next, size) => patch({ modelPage: String(size === modelPageSize ? next : 1), modelPageSize: String(size) })} /></>
              )}
            </div>

            <div className="section">
              <div className="section-title"><h2>期间明细</h2></div>
              <Table<AdminUsageItem>
                className="usage-table"
                rowKey="id"
                columns={itemColumns}
                dataSource={summary.items}
                pagination={{
                  ...usagePagination,
                  current: summary.page,
                  pageSize: summary.pageSize,
                  total: summary.total,
                  onChange: (next, size) => patch({ page: String(size === pageSize ? next : 1), pageSize: String(size) }),
                }}
                scroll={{ x: 720 }}
                size="small"
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
